import type { RoadmapDTO, RoadmapInsertDTO, RoadmapUpdateDTO } from '@tempo-io/roadmap-dto';
import { tempoApi } from '../tempoApi';
import type {
  RoadmapOwnerResponse,
  RoadmapUserPermissions,
  RoadmapUsersPermissionResponse,
  SetRoadmapPermissionsRequest,
  ValidateRoadmapOwnerTransferResponse,
} from '../types/roadmapping/roadmap-permissions';

const fetchRoadmaps = async (): Promise<RoadmapDTO[]> => {
  return tempoApi().get('api/v1/roadmap').json<RoadmapDTO[]>();
};

const fetchRoadmap = async (roadmapId: number): Promise<RoadmapDTO> => {
  return tempoApi().get(`api/v1/roadmap/${roadmapId}`).json<RoadmapDTO>();
};

const createRoadmap = async (data: RoadmapInsertDTO): Promise<RoadmapDTO> => {
  return tempoApi()
    .post('api/v1/roadmap', {
      json: data,
    })
    .json<RoadmapDTO>();
};

const updateRoadmap = async (roadmapId: number, data: RoadmapUpdateDTO): Promise<RoadmapDTO> => {
  return tempoApi()
    .put(`api/v1/roadmap/${roadmapId}`, {
      json: data,
    })
    .json<RoadmapDTO>();
};

const deleteRoadmap = async (roadmapId: number): Promise<void> => {
  await tempoApi().delete(`api/v1/roadmap/${roadmapId}`);
};

const getUserRoadmapPermissions = async (
  roadmapId: number,
): Promise<RoadmapUsersPermissionResponse> => {
  return tempoApi()
    .get(`api/v1/roadmap/${roadmapId}/permissions`)
    .json<RoadmapUsersPermissionResponse>();
};

const setUserRoadmapPermissions = async (
  roadmapId: number,
  data: SetRoadmapPermissionsRequest,
): Promise<void> => {
  await tempoApi().put(`api/v1/roadmap/${roadmapId}/permissions`, {
    json: data,
  });
};

const getRoadmapOwner = async (roadmapId: number): Promise<RoadmapOwnerResponse> => {
  return tempoApi().get(`api/v1/roadmap/${roadmapId}/owner`).json<RoadmapOwnerResponse>();
};

const getRoadmapUserPermissions = async (roadmapId: number): Promise<RoadmapUserPermissions> => {
  return tempoApi()
    .get(`api/v1/roadmap/${roadmapId}/user-permissions`)
    .json<RoadmapUserPermissions>();
};

const transferRoadmapOwnership = async (roadmapId: number, newOwnerId: string): Promise<void> => {
  await tempoApi()
    .post(`api/v1/roadmap/${roadmapId}/owner`, {
      json: { newOwnerId },
    })
    .json<void>();
};

const getOwnedRoadmapsCount = async (userId: string): Promise<{ count: number }> => {
  return tempoApi()
    .get(`api/v1/roadmap/admin/users/${userId}/owned-roadmaps-count`)
    .json<{ count: number }>();
};

const validateRoadmapOwnerTransfer = async (
  roadmapId: number,
  newOwnerId: string,
): Promise<ValidateRoadmapOwnerTransferResponse> => {
  return tempoApi()
    .post(`api/v1/roadmap/${roadmapId}/owner/validate`, {
      json: { newOwnerId },
    })
    .json<ValidateRoadmapOwnerTransferResponse>();
};

export const RoadmapApi = {
  fetchRoadmaps,
  fetchRoadmap,
  createRoadmap,
  updateRoadmap,
  deleteRoadmap,
  getUserRoadmapPermissions,
  setUserRoadmapPermissions,
  getRoadmapOwner,
  getRoadmapUserPermissions,
  transferRoadmapOwnership,
  validateRoadmapOwnerTransfer,
  getOwnedRoadmapsCount,
} as const;
